import React, { useMemo } from 'react';
import {
  AtomicDialog,
  Button,
  ColorNameEnum,
  SizeEnum,
  Text,
  useDialogState,
} from '@getvim/atomic-ui';
import './UhcPremiumPhysicianProgramModal.less';
import { analyticsManager } from '../../../../analytics/analyticsManager';
import { sanitizeDisclaimerHtml } from '../../utils/sanitizeHtml';

interface UhcPremiumPhysicianProgramModalProps {
  disclaimerHtml?: string;
}

export const UhcPremiumPhysicianProgramModal: React.FC<UhcPremiumPhysicianProgramModalProps> = ({
  disclaimerHtml,
}) => {
  const dialog = useDialogState();
  const sanitizedDisclaimer = useMemo(
    () => (disclaimerHtml ? sanitizeDisclaimerHtml(disclaimerHtml).trim() : ''),
    [disclaimerHtml],
  );

  return (
    <AtomicDialog
      dialog={dialog}
      disclosure={
        <Text
          inline
          colorName={ColorNameEnum.ultraDark}
          size={SizeEnum['12px']}
          className="uhc-premium-physician-program-modal-disclosure"
          onClick={() => analyticsManager.track('performance_score_hyperlink_clicked', {})}
        >
          What is UnitedHealth Premium Care Physician?
        </Text>
      }
      title="UnitedHealth Premium® Program"
      footerContent={
        <a
          href="https://www.uhcprovider.com/en/reports-quality-programs/premium-designation.html"
          target="_blank"
          style={{ flex: '1 1 auto', display: 'flex' }}
          rel="noreferrer"
        >
          <Button
            buttonType="tiny"
            bgColor="themedOutline"
            style={{ flexGrow: 1 }}
            onClick={() => {
              analyticsManager.track('performance_score_modal_button_clicked', {});
              dialog.hide();
            }}
          >
            Learn more
          </Button>
        </a>
      }
    >
      <div style={{ fontSize: '12px' }}>
        {sanitizedDisclaimer ? (
          // eslint-disable-next-line react/no-danger
          <div dangerouslySetInnerHTML={{ __html: sanitizedDisclaimer }} />
        ) : (
          <>
            <p>
              A Premium Care Physician meets the UnitedHealth Premium quality care criteria which
              includes safe, timely, effective and efficient care.
            </p>
            <p>
              UnitedHealth Premium® is proprietary to UnitedHealthcare. UnitedHealth Premium
              evaluates physicians based on safe, timely, effective and efficient quality care
              criteria to help members make more informed choices for their health care.{' '}
              <b>
                It's intended only as a guide and should not be the sole factor considered when
                selecting a physician. Designations have a risk of error and members should discuss
                designations with a physician before choosing one. If a member already has a
                physician, they should also consult with them for advice on selecting other
                physicians.
              </b>{' '}
              The fact that a physician does not have a Premium Care Physician designation does not
              mean that the physician does not provide quality health care services. All physicians
              in the UnitedHealthcare network have met certain minimum credentialing requirements
              (separate from the UnitedHealth Premium criteria). Please visit the medical care
              directory specific to the member's benefit plan for physician designations and
              detailed information about UnitedHealth Premium and the evaluation methodology.
            </p>
          </>
        )}
      </div>
    </AtomicDialog>
  );
};
